import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { t } from '@/i18n';
import {
  MIND_SEARCH_DEBOUNCE_MS,
  canonicalMindSkill,
  describeMindHostGap,
  describeMindSkillGap,
  filterMindSkills,
  formatMindMention,
  formatMindSkill,
  getMindHost,
  isMindNodeHit,
  isMindSkill,
  listMindEntries,
  listMindSkills,
  mindNodeBreadcrumb,
  mindNodeHasChildren,
  mindNodePathLabel,
  mindNodeSelectable,
  searchMindNodes,
  type MindNodeHit,
  type MindNodeType,
  type MindSkill,
  type MindSkillListResult,
} from '@/utils/mindHost';
import { MindNodeIcon, mindNodeIconMask } from './mindNodeIcon';
import './mindMention.less';

const MIN_HEIGHT = 72;
const MAX_HEIGHT = 240;

const TYPE_LABEL_KEY: Record<MindNodeType, string> = {
  doc_space: 'mind.type.docSpace',
  project: 'mind.type.project',
  folder: 'mind.type.folder',
  doc: 'mind.type.doc',
  file: 'mind.type.file',
};

type MenuPhase = 'hint' | 'loading' | 'ready' | 'empty' | 'emptyDir' | 'unavailable';

interface MenuState {
  open: boolean;
  items: MindNodeHit[];
  phase: MenuPhase;
  activeIndex: number;
  /** 进入本层的父路径。根层和检索结果为空。 */
  breadcrumb: string;
  /** 0 是栈底（一级入口或检索结果）。 */
  depth: number;
  query: string;
}

interface BrowseFrame {
  items: MindNodeHit[];
  phase: MenuPhase;
  activeIndex: number;
  breadcrumb: string;
  depth: number;
  query: string;
}

const CLOSED_MENU: MenuState = {
  open: false,
  items: [],
  phase: 'hint',
  activeIndex: 0,
  breadcrumb: '',
  depth: 0,
  query: '',
};

function listPhase(items: MindNodeHit[], partial: boolean, child: boolean): MenuPhase {
  if (items.length > 0) return 'ready';
  if (partial) return 'unavailable';
  return child ? 'emptyDir' : 'hint';
}

type SkillPhase = 'loading' | 'ready' | 'empty' | 'unavailable';

interface SkillMenuState {
  open: boolean;
  items: MindSkill[];
  phase: SkillPhase;
  activeIndex: number;
  query: string;
}

const CLOSED_SKILL: SkillMenuState = {
  open: false,
  items: [],
  phase: 'loading',
  activeIndex: 0,
  query: '',
};

function skillListPhase(items: MindSkill[], partial: boolean, sourceCount: number): SkillPhase {
  if (items.length > 0) return 'ready';
  if (partial && sourceCount === 0) return 'unavailable';
  return 'empty';
}

export interface MindMentionEditorHandle {
  clear: () => void;
}

interface MindMentionEditorProps {
  disabled?: boolean;
  maxLength: number;
  placeholder: string;
  onChange: (apiText: string) => void;
  onSubmit: (apiText: string) => void;
  onHeight: (height: number) => void;
}

function isChip(node: Node | null): node is HTMLElement {
  return node instanceof HTMLElement && Boolean(node.dataset.mindHit || node.dataset.mindSkill);
}

function readChipHit(node: HTMLElement): MindNodeHit | null {
  const raw = node.dataset.mindHit;
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    return isMindNodeHit(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function createChip(hit: MindNodeHit): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'mind-mention';
  span.contentEditable = 'false';
  span.dataset.mindHit = encodeURIComponent(JSON.stringify({
    id: hit.id,
    type: hit.type,
    spaceId: hit.spaceId,
    name: hit.name,
    path: hit.path ?? [],
  }));
  const icon = document.createElement('span');
  icon.className = 'mind-node-icon';
  icon.setAttribute('aria-hidden', 'true');
  const mask = mindNodeIconMask(hit.type);
  icon.style.maskImage = mask;
  icon.style.webkitMaskImage = mask;
  const label = document.createElement('span');
  label.className = 'mind-mention__label';
  label.textContent = `@${hit.name || hit.id}`;
  span.append(icon, label);
  return span;
}

function readSkillChip(node: HTMLElement): MindSkill | null {
  const raw = node.dataset.mindSkill;
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    return isMindSkill(parsed) ? canonicalMindSkill(parsed) : null;
  } catch {
    return null;
  }
}

function createSkillChip(skill: MindSkill): HTMLSpanElement {
  const canonical = canonicalMindSkill(skill);
  const span = document.createElement('span');
  span.className = 'mind-mention';
  span.contentEditable = 'false';
  span.dataset.mindSkill = encodeURIComponent(JSON.stringify(canonical));
  const label = document.createElement('span');
  label.className = 'mind-mention__label';
  label.textContent = `/${canonical.name || canonical.id}`;
  span.append(label);
  return span;
}

function serializeEditor(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent ?? '').replace(/\u00a0/g, ' ');
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.dataset.mindSkill) {
      const skill = readSkillChip(node);
      if (skill) out += formatMindSkill(skill);
      return;
    }
    if (node.dataset.mindHit) {
      const hit = readChipHit(node);
      if (hit) out += formatMindMention(hit);
      return;
    }
    if (node.tagName === 'BR') {
      out += '\n';
      return;
    }
    const block = node.tagName === 'DIV' || node.tagName === 'P';
    if (block && out && !out.endsWith('\n')) out += '\n';
    node.childNodes.forEach(walk);
  };
  root.childNodes.forEach(walk);
  return out.replace(/\n$/, '');
}

function isVisuallyEmpty(root: HTMLElement): boolean {
  if (root.querySelector('[data-mind-hit],[data-mind-skill]')) return false;
  return serializeEditor(root).trim().length === 0;
}

interface ActiveTokenQuery {
  kind: 'mention' | 'skill';
  textNode: Text;
  at: number;
  caret: number;
}

/** 行首或空白后的 @ / / 才算触发。两者都合法时用更靠近光标的那个。 */
function markerIndex(before: string, marker: string): number {
  const index = before.lastIndexOf(marker);
  if (index < 0) return -1;
  if (index > 0 && !/\s/.test(before[index - 1] ?? '')) return -1;
  const query = before.slice(index + 1);
  if (/[\r\n]/.test(query)) return -1;
  return index;
}

function getActiveTokenQuery(root: HTMLElement): ActiveTokenQuery | null {
  const selection = window.getSelection();
  if (!selection || !selection.isCollapsed || !selection.anchorNode) return null;
  if (!root.contains(selection.anchorNode)) return null;

  let textNode: Text | null = null;
  let caret = 0;
  if (selection.anchorNode.nodeType === Node.TEXT_NODE) {
    textNode = selection.anchorNode as Text;
    caret = selection.anchorOffset;
  } else if (selection.anchorNode instanceof HTMLElement) {
    const previous = selection.anchorNode.childNodes[selection.anchorOffset - 1];
    if (previous?.nodeType === Node.TEXT_NODE) {
      textNode = previous as Text;
      caret = textNode.data.length;
    }
  }
  if (!textNode) return null;

  const before = textNode.data.slice(0, caret);
  const atIndex = markerIndex(before, '@');
  const slashIndex = markerIndex(before, '/');
  if (atIndex < 0 && slashIndex < 0) return null;
  const kind = slashIndex > atIndex ? 'skill' : 'mention';
  return {
    kind,
    textNode,
    at: kind === 'skill' ? slashIndex : atIndex,
    caret,
  };
}

function placeCaret(node: Node, offset: number): void {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  const max = node.nodeType === Node.TEXT_NODE ? (node.textContent ?? '').length : node.childNodes.length;
  range.setStart(node, Math.min(offset, max));
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

function placeCaretAtEnd(root: HTMLElement): void {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(root);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

function insertTextAtSelection(text: string): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  placeCaret(node, node.data.length);
}

function chipBesideCaret(direction: 'before' | 'after'): HTMLElement | null {
  const selection = window.getSelection();
  if (!selection || !selection.isCollapsed || !selection.anchorNode) return null;
  const node = selection.anchorNode;
  const offset = selection.anchorOffset;
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    if (direction === 'before' && offset === 0 && isChip(node.previousSibling)) return node.previousSibling;
    if (direction === 'after' && offset === text.length && isChip(node.nextSibling)) return node.nextSibling;
    return null;
  }
  if (node instanceof HTMLElement) {
    const sibling = (direction === 'before' ? node.childNodes[offset - 1] : node.childNodes[offset]) ?? null;
    if (isChip(sibling)) return sibling;
  }
  return null;
}

function insertToken(root: HTMLElement, query: ActiveTokenQuery, chip: HTMLElement): void {
  const { textNode, at, caret } = query;
  if (!textNode.isConnected) return;
  const parent = textNode.parentNode;
  if (!parent) return;
  const before = textNode.data.slice(0, at);
  const after = textNode.data.slice(caret);
  const afterText = after.length === 0 ? ' ' : after;
  if (before) parent.insertBefore(document.createTextNode(before), textNode);
  parent.insertBefore(chip, textNode);
  const afterNode = document.createTextNode(afterText);
  parent.insertBefore(afterNode, textNode);
  textNode.remove();
  root.focus();
  placeCaret(afterNode, after.length === 0 ? 1 : 0);
}

const MindMentionEditor = forwardRef<MindMentionEditorHandle, MindMentionEditorProps>(
  function MindMentionEditor({ disabled, maxLength, placeholder, onChange, onSubmit, onHeight }, ref) {
    const editorRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const snapshotRef = useRef('');
    const requestRef = useRef(0);
    const timerRef = useRef<number | undefined>(undefined);
    const composingRef = useRef(false);
    const keywordRef = useRef<string | null>(null);
    const hostGapRef = useRef<string | null>(null);
    const menuOpenRef = useRef(false);
    const menuStateRef = useRef<MenuState>(CLOSED_MENU);
    const browseStackRef = useRef<BrowseFrame[]>([]);
    const drillLockRef = useRef(false);
    const onChangeRef = useRef(onChange);
    const onSubmitRef = useRef(onSubmit);
    const onHeightRef = useRef(onHeight);
    const skillMenuRef = useRef<SkillMenuState>(CLOSED_SKILL);
    const skillRequestRef = useRef(0);
    const skillCacheRef = useRef<MindSkillListResult | null>(null);
    const skillKeywordRef = useRef<string | null>(null);
    const skillGapRef = useRef<string | null>(null);
    const skillLoadingRef = useRef(false);
    const [empty, setEmpty] = useState(true);
    const [menu, setMenu] = useState<MenuState>(CLOSED_MENU);
    const [skillMenu, setSkillMenu] = useState<SkillMenuState>(CLOSED_SKILL);

    onChangeRef.current = onChange;
    onSubmitRef.current = onSubmit;
    onHeightRef.current = onHeight;
    menuStateRef.current = menu;
    skillMenuRef.current = skillMenu;

    const measure = () => {
      const root = editorRef.current;
      if (!root) return;
      root.style.height = 'auto';
      const next = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, root.scrollHeight));
      root.style.height = `${next}px`;
      onHeightRef.current(next);
    };

    const closeMentionMenu = () => {
      requestRef.current += 1;
      window.clearTimeout(timerRef.current);
      keywordRef.current = null;
      menuOpenRef.current = false;
      browseStackRef.current = [];
      drillLockRef.current = false;
      setMenu(CLOSED_MENU);
    };

    const closeSkillMenu = () => {
      skillRequestRef.current += 1;
      skillLoadingRef.current = false;
      skillCacheRef.current = null;
      skillKeywordRef.current = null;
      setSkillMenu((prev) => (prev.open ? CLOSED_SKILL : prev));
    };

    const closeMenu = () => {
      closeMentionMenu();
      closeSkillMenu();
    };

    const syncSkillQuery = (query: ActiveTokenQuery) => {
      const keyword = query.textNode.data.slice(query.at + 1, query.caret);
      const gap = describeMindSkillGap();
      if (gap) {
        if (skillGapRef.current !== gap) {
          skillGapRef.current = gap;
          console.warn('[Macaw] / 未打开技能列表', gap);
        }
        closeMenu();
        return;
      }
      skillGapRef.current = null;
      closeMentionMenu();

      const cached = skillCacheRef.current;
      if (cached) {
        if (skillKeywordRef.current === keyword && skillMenuRef.current.open) return;
        skillKeywordRef.current = keyword;
        const items = filterMindSkills(cached.skills, keyword);
        setSkillMenu({
          open: true,
          items,
          phase: skillListPhase(items, cached.partial, cached.skills.length),
          activeIndex: 0,
          query: keyword,
        });
        return;
      }

      if (skillLoadingRef.current) {
        skillKeywordRef.current = keyword;
        setSkillMenu((prev) => (prev.query === keyword ? prev : { ...prev, query: keyword }));
        return;
      }

      skillKeywordRef.current = keyword;
      skillLoadingRef.current = true;
      const requestId = skillRequestRef.current + 1;
      skillRequestRef.current = requestId;
      setSkillMenu({ open: true, items: [], phase: 'loading', activeIndex: 0, query: keyword });
      void listMindSkills().then((result) => {
        if (requestId !== skillRequestRef.current) return;
        skillLoadingRef.current = false;
        if (!result) {
          closeSkillMenu();
          return;
        }
        skillCacheRef.current = result;
        const currentKeyword = skillKeywordRef.current ?? '';
        const items = filterMindSkills(result.skills, currentKeyword);
        setSkillMenu({
          open: true,
          items,
          phase: skillListPhase(items, result.partial, result.skills.length),
          activeIndex: 0,
          query: currentKeyword,
        });
      });
    };

    const rememberFrame = (current: MenuState): BrowseFrame => ({
      items: current.items,
      phase: current.phase,
      activeIndex: current.activeIndex,
      breadcrumb: current.breadcrumb,
      depth: current.depth,
      query: current.query,
    });

    const openChild = (hit: MindNodeHit) => {
      if (!mindNodeHasChildren(hit) || drillLockRef.current || disabled) return;
      const current = menuStateRef.current;
      if (!current.open) return;
      drillLockRef.current = true;
      browseStackRef.current.push(rememberFrame(current));
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      const breadcrumb = mindNodeBreadcrumb(hit);
      const depth = current.depth + 1;
      setMenu({
        open: true,
        items: [],
        phase: 'loading',
        activeIndex: 0,
        breadcrumb,
        depth,
        query: current.query,
      });
      void listMindEntries(hit).then((result) => {
        if (requestId !== requestRef.current) return;
        drillLockRef.current = false;
        if (!result) {
          const prev = browseStackRef.current.pop();
          if (prev) setMenu({ open: true, ...prev });
          else closeMenu();
          return;
        }
        setMenu({
          open: true,
          items: result.items,
          phase: listPhase(result.items, result.partial, true),
          activeIndex: 0,
          breadcrumb,
          depth,
          query: current.query,
        });
      });
    };

    const goBack = () => {
      const prev = browseStackRef.current.pop();
      if (!prev) return false;
      requestRef.current += 1;
      drillLockRef.current = false;
      setMenu({ open: true, ...prev });
      editorRef.current?.focus();
      return true;
    };

    const commitEditor = (search: boolean) => {
      const root = editorRef.current;
      if (!root) return;
      let text = serializeEditor(root);
      if (text.length > maxLength) {
        root.innerHTML = snapshotRef.current;
        placeCaretAtEnd(root);
        text = serializeEditor(root);
      } else {
        snapshotRef.current = root.innerHTML;
      }
      setEmpty(isVisuallyEmpty(root));
      onChangeRef.current(text);
      measure();
      if (search && !composingRef.current) syncQuery();
    };

    const syncQuery = () => {
      const root = editorRef.current;
      if (!root || disabled) {
        closeMenu();
        return;
      }
      const active = getActiveTokenQuery(root);
      if (!active) {
        closeMenu();
        return;
      }
      if (active.kind === 'skill') {
        syncSkillQuery(active);
        return;
      }
      closeSkillMenu();
      const keyword = active.textNode.data.slice(active.at + 1, active.caret);
      // 宿主没挂上时 @ 只是普通字符，不打开列表，原因只打到控制台
      const hostGap = describeMindHostGap();
      if (hostGap) {
        if (hostGapRef.current !== hostGap) {
          hostGapRef.current = hostGap;
          console.warn('[Macaw] @ 未打开列表', hostGap);
        }
        closeMenu();
        return;
      }
      hostGapRef.current = null;
      if (keywordRef.current === keyword && menuOpenRef.current) return;
      keywordRef.current = keyword;
      menuOpenRef.current = true;
      browseStackRef.current = [];
      drillLockRef.current = false;
      window.clearTimeout(timerRef.current);
      if (!keyword.trim()) {
        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        if (typeof getMindHost()?.listEntries !== 'function') {
          setMenu({ open: true, items: [], phase: 'hint', activeIndex: 0, breadcrumb: '', depth: 0, query: keyword });
          return;
        }
        setMenu({ open: true, items: [], phase: 'loading', activeIndex: 0, breadcrumb: '', depth: 0, query: keyword });
        void listMindEntries().then((result) => {
          if (requestId !== requestRef.current) return;
          if (!result) {
            setMenu({ open: true, items: [], phase: 'hint', activeIndex: 0, breadcrumb: '', depth: 0, query: keyword });
            return;
          }
          setMenu({
            open: true,
            items: result.items,
            phase: listPhase(result.items, result.partial, false),
            activeIndex: 0,
            breadcrumb: '',
            depth: 0,
            query: keyword,
          });
        });
        return;
      }
      setMenu({ open: true, items: [], phase: 'loading', activeIndex: 0, breadcrumb: '', depth: 0, query: keyword });
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      timerRef.current = window.setTimeout(() => {
        void searchMindNodes(keyword).then((result) => {
          if (requestId !== requestRef.current) return;
          if (!result) {
            closeMenu();
            return;
          }
          const phase: MenuPhase = result.items.length > 0
            ? 'ready'
            : result.partial
              ? 'unavailable'
              : 'empty';
          setMenu({
            open: true,
            items: result.items,
            phase,
            activeIndex: 0,
            breadcrumb: '',
            depth: 0,
            query: keyword,
          });
        });
      }, MIND_SEARCH_DEBOUNCE_MS);
    };

    const selectHit = (hit: MindNodeHit) => {
      const root = editorRef.current;
      if (!root || disabled || !mindNodeSelectable(hit)) return;
      const query = getActiveTokenQuery(root);
      if (!query || query.kind !== 'mention') {
        closeMenu();
        return;
      }
      insertToken(root, query, createChip(hit));
      closeMenu();
      commitEditor(false);
    };

    const selectSkill = (skill: MindSkill) => {
      const root = editorRef.current;
      if (!root || disabled) return;
      const query = getActiveTokenQuery(root);
      if (!query || query.kind !== 'skill') {
        closeMenu();
        return;
      }
      insertToken(root, query, createSkillChip(skill));
      closeMenu();
      commitEditor(false);
    };

    useImperativeHandle(ref, () => ({
      clear() {
        const root = editorRef.current;
        if (!root) return;
        root.innerHTML = '';
        snapshotRef.current = '';
        setEmpty(true);
        closeMenu();
        onChangeRef.current('');
        measure();
      },
    }));

    useLayoutEffect(() => {
      measure();
    }, []);

    useEffect(() => {
      editorRef.current?.focus();
    }, []);

    useEffect(() => () => {
      window.clearTimeout(timerRef.current);
    }, []);

    useEffect(() => {
      if (disabled) closeMenu();
    }, [disabled]);

    useEffect(() => {
      if (!menu.open && !skillMenu.open) return;
      const onPointerDown = (event: MouseEvent) => {
        const target = event.target as Node;
        if (menuRef.current?.contains(target) || editorRef.current?.contains(target)) return;
        closeMenu();
      };
      document.addEventListener('mousedown', onPointerDown);
      return () => {
        document.removeEventListener('mousedown', onPointerDown);
      };
    }, [menu.open, skillMenu.open]);

    useEffect(() => {
      const option = menuRef.current?.querySelector<HTMLElement>('[data-active="true"]');
      option?.scrollIntoView({ block: 'nearest' });
    }, [menu.activeIndex, menu.items, skillMenu.activeIndex, skillMenu.items]);

    const moveActive = (delta: number) => {
      setMenu((prev) => {
        if (!prev.open || prev.items.length === 0) return prev;
        const next = Math.min(prev.items.length - 1, Math.max(0, prev.activeIndex + delta));
        return { ...prev, activeIndex: next };
      });
    };

    const moveSkill = (delta: number) => {
      setSkillMenu((prev) => {
        if (!prev.open || prev.items.length === 0) return prev;
        const next = Math.min(prev.items.length - 1, Math.max(0, prev.activeIndex + delta));
        return { ...prev, activeIndex: next };
      });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.nativeEvent.isComposing || event.keyCode === 229) return;

      if (event.key === 'Escape' && (menu.open || skillMenu.open)) {
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
        closeMenu();
        return;
      }

      if (skillMenu.open && skillMenu.items.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        moveSkill(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (skillMenu.open && skillMenu.items.length > 0 && event.key === 'Tab') {
        event.preventDefault();
        const skill = skillMenu.items[skillMenu.activeIndex];
        if (skill) selectSkill(skill);
        return;
      }

      if (skillMenu.open && event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (disabled) return;
        if (skillMenu.items.length > 0) {
          const skill = skillMenu.items[skillMenu.activeIndex];
          if (skill) selectSkill(skill);
          return;
        }
        if (skillMenu.phase === 'loading') return;
        const text = editorRef.current ? serializeEditor(editorRef.current) : '';
        closeMenu();
        onSubmitRef.current(text);
        return;
      }

      if (menu.open && menu.items.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        moveActive(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (menu.open && event.key === 'ArrowRight') {
        const hit = menu.items[menu.activeIndex];
        const atBrowseRoot = menu.depth === 0 && !menu.query.trim();
        if (hit && mindNodeHasChildren(hit)) {
          event.preventDefault();
          event.stopPropagation();
          if (!event.repeat) openChild(hit);
          return;
        }
        if (atBrowseRoot || menu.depth > 0) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }

      if (menu.open && event.key === 'ArrowLeft') {
        if (menu.depth > 0) {
          event.preventDefault();
          event.stopPropagation();
          if (!event.repeat) goBack();
          return;
        }
        if (!menu.query.trim()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }

      if (menu.open && menu.items.length > 0 && event.key === 'Tab') {
        event.preventDefault();
        const hit = menu.items[menu.activeIndex];
        if (hit && mindNodeSelectable(hit)) selectHit(hit);
        return;
      }

      if (event.key === 'Backspace' && !event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const chip = chipBesideCaret('before');
        if (chip) {
          event.preventDefault();
          chip.remove();
          commitEditor(true);
          return;
        }
      }

      if (event.key === 'Delete' && !event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const chip = chipBesideCaret('after');
        if (chip) {
          event.preventDefault();
          chip.remove();
          commitEditor(true);
          return;
        }
      }

      if (event.key !== 'Enter' || event.shiftKey) {
        if (event.key === 'Enter' && event.shiftKey) {
          event.preventDefault();
          insertTextAtSelection('\n');
          commitEditor(true);
        }
        return;
      }

      event.preventDefault();
      if (disabled) return;
      if (menu.open && menu.items.length > 0) {
        const hit = menu.items[menu.activeIndex];
        if (hit && mindNodeSelectable(hit)) selectHit(hit);
        return;
      }
      if (menu.open && (menu.phase === 'loading' || menu.depth > 0)) return;
      const text = editorRef.current ? serializeEditor(editorRef.current) : '';
      closeMenu();
      onSubmitRef.current(text);
    };

    const statusText = menu.phase === 'hint'
      ? t('chatInput.mentionHint')
      : menu.phase === 'loading'
        ? t('chatInput.mentionLoading')
        : menu.phase === 'unavailable'
          ? t('chatInput.mentionUnavailable')
          : menu.phase === 'empty'
            ? t('chatInput.mentionEmpty')
            : menu.phase === 'emptyDir'
              ? t('chatInput.mentionEmptyDir')
              : '';

    const menuNode = menu.open ? (
      <div
        ref={menuRef}
        className="mind-mention-menu"
        onMouseDown={(event) => event.preventDefault()}
        onPointerDown={(event) => event.preventDefault()}
      >
        {menu.depth > 0 ? (
          <button
            type="button"
            className="mind-mention-menu__crumb"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              goBack();
            }}
          >
            <span aria-hidden>←</span>
            <span className="mind-mention-menu__crumb-label">
              {menu.breadcrumb || t('chatInput.mentionBack')}
            </span>
          </button>
        ) : null}
        <div className="mind-mention-menu__list" id="mind-mention-list" role="listbox">
          {menu.items.map((item, index) => {
            const active = index === menu.activeIndex;
            const pathLabel = menu.depth === 0 ? mindNodePathLabel(item.path) : '';
            const canOpen = mindNodeHasChildren(item);
            const selectable = mindNodeSelectable(item);
            return (
              <div
                key={`${item.type}:${item.spaceId}:${item.id}`}
                id={`mind-mention-opt-${index}`}
                role="option"
                aria-selected={active && selectable}
                aria-disabled={!selectable}
                data-active={active ? 'true' : 'false'}
                className={`mind-mention-item${active ? ' mind-mention-item--active' : ''}${selectable ? '' : ' mind-mention-item--static'}`}
                onMouseEnter={() => setMenu((prev) => ({ ...prev, activeIndex: index }))}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!selectable) return;
                  selectHit(item);
                }}
              >
                <MindNodeIcon type={item.type} />
                <span className="mind-mention-item__name">{item.name || item.id}</span>
                {pathLabel ? (
                  <span className="mind-mention-item__path" title={pathLabel}>
                    {pathLabel}
                  </span>
                ) : null}
                <span className="mind-mention-item__type">{t(TYPE_LABEL_KEY[item.type])}</span>
                {canOpen ? <span className="mind-mention-item__more" aria-hidden>›</span> : null}
              </div>
            );
          })}
          {menu.items.length === 0 && statusText ? (
            <div className="mind-mention-menu__status">{statusText}</div>
          ) : null}
        </div>
        {menu.items.length > 0 || menu.depth > 0 ? (
          <div className="mind-mention-menu__nav">
            {menu.depth > 0 || !menu.query.trim()
              ? t('chatInput.mentionNavBrowse')
              : t('chatInput.mentionNav')}
          </div>
        ) : null}
      </div>
    ) : null;

    const skillStatusText = skillMenu.phase === 'loading'
      ? t('chatInput.skillLoading')
      : skillMenu.phase === 'unavailable'
        ? t('chatInput.skillUnavailable')
        : skillMenu.phase === 'empty'
          ? t(skillMenu.query.trim() ? 'chatInput.skillEmpty' : 'chatInput.skillNone')
          : '';

    const skillMenuNode = skillMenu.open ? (
      <div
        ref={menuRef}
        className="mind-mention-menu"
        onMouseDown={(event) => event.preventDefault()}
        onPointerDown={(event) => event.preventDefault()}
      >
        <div className="mind-mention-menu__list" id="mind-skill-list" role="listbox">
          {skillMenu.items.map((item, index) => {
            const active = index === skillMenu.activeIndex;
            return (
              <div
                key={item.id}
                id={`mind-skill-opt-${index}`}
                role="option"
                aria-selected={active}
                data-active={active ? 'true' : 'false'}
                className={`mind-mention-item${active ? ' mind-mention-item--active' : ''}`}
                onMouseEnter={() => setSkillMenu((prev) => ({ ...prev, activeIndex: index }))}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  selectSkill(item);
                }}
              >
                <span className="mind-mention-item__name">{item.name}</span>
              </div>
            );
          })}
          {skillMenu.items.length === 0 && skillStatusText ? (
            <div className="mind-mention-menu__status">{skillStatusText}</div>
          ) : null}
        </div>
        {skillMenu.items.length > 0 ? (
          <div className="mind-mention-menu__nav">{t('chatInput.skillNav')}</div>
        ) : null}
      </div>
    ) : null;

    return (
      <div className="mind-mention-editor-wrap">
        {empty ? <div className="mind-mention-placeholder">{placeholder}</div> : null}
        <div
          ref={editorRef}
          className="chat-input-textarea mind-mention-editor"
          contentEditable={disabled ? 'false' : 'true'}
          role="textbox"
          aria-multiline="true"
          aria-placeholder={placeholder}
          aria-expanded={menu.open || skillMenu.open}
          aria-controls={menu.open ? 'mind-mention-list' : skillMenu.open ? 'mind-skill-list' : undefined}
          aria-activedescendant={
            menu.open && menu.items.length > 0
              ? `mind-mention-opt-${menu.activeIndex}`
              : skillMenu.open && skillMenu.items.length > 0
                ? `mind-skill-opt-${skillMenu.activeIndex}`
                : undefined
          }
          aria-autocomplete="list"
          spellCheck={false}
          suppressContentEditableWarning
          onInput={() => commitEditor(!composingRef.current)}
          onKeyDown={handleKeyDown}
          onKeyUp={(event) => {
            if (composingRef.current) return;
            if (event.key === 'Escape' || event.key === 'Enter' || event.key === 'Tab') return;
            if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && (menu.items.length > 0 || skillMenu.items.length > 0)) return;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              if (skillMenu.open) {
                syncQuery();
                return;
              }
              if (menu.depth > 0 || !menu.query.trim()) return;
            }
            syncQuery();
          }}
          onClick={() => {
            if (!composingRef.current) syncQuery();
          }}
          onCompositionStart={() => {
            composingRef.current = true;
          }}
          onCompositionEnd={() => {
            composingRef.current = false;
            commitEditor(true);
          }}
          onPaste={(event) => {
            event.preventDefault();
            const text = event.clipboardData.getData('text/plain');
            if (!text || disabled) return;
            insertTextAtSelection(text);
            commitEditor(true);
          }}
          onDrop={(event) => {
            event.preventDefault();
            const text = event.dataTransfer.getData('text/plain');
            if (!text || disabled) return;
            insertTextAtSelection(text);
            commitEditor(true);
          }}
          onBeforeInput={(event) => {
            const inputType = (event.nativeEvent as InputEvent).inputType;
            if (inputType === 'insertParagraph' || inputType === 'insertLineBreak') {
              event.preventDefault();
            }
          }}
        />
        {menuNode}
        {skillMenuNode}
      </div>
    );
  },
);

export default MindMentionEditor;
